import { createSlice } from "@reduxjs/toolkit";
import Enums from "../../enums";
// import api from "../../Services/ApiServices";
import api from "../../Services/ApiServices";
import lsu from "../../Services/LocalStorageUtils";
import { IAOPR, IAOR } from "../../Types/AccountOpeningRequest";
import { IDataSetApiResponse } from "../../Types/AllTypes";
import AuthInfoContext from "../../Types/AuthInfoContext";
import {
  ImpersonateLoginInsertObject,
  SetNameEmailInsertObject,
} from "../../Types/InsertObjects";
// import AuthInfoContext from "../../Types/AuthInfoContext";

import { IOTPSession } from "../../Types/OTPSession";
import { dispatch, RootState } from "../store";
import {
  logOutAndResetFields,
  setAuthInfo,
  setSelectedAOPR,
  setSelectedAOR,
  setStage,
} from "./common";

export enum RegistrationStep {
  SIGN_UP_NOW = "SIGN_UP_NOW",
  CONFIRM_OTP = "CONFIRM_OTP",
  YOUR_DETAILS = "YOUR_DETAILS",
  SELECT_UNFINISHED_AOR = "SELECT_UNFINISHED_AOR",
  CONFIRM_EMAIL_OTP = "CONFIRM_EMAIL_OTP",
  START_WITH_PAN = "START_WITH_PAN",
  PAY_ACCOUNT_OPENING_FEE = "PAY_ACCOUNT_OPENING_FEE",
  CHANGE_MOBILE_NUMBER = "CHANGE_MOBILE_NUMBER",
  CONFIRMED_CHANGED_MOBILE = "CONFIRMED_CHANGED_MOBILE",
}

interface IRegistrationState {
  isLoading: boolean;
  error: any;
  registrationStep: RegistrationStep;
  mobileOTPResponse?: IOTPSession;
  mobileNo?: number;
  email?: string;
  otpSendDateTime?: Date;
  dsUnfinishedRequest?: IDataSetApiResponse;
  isImpersonatedSession?: boolean;

  resendOTPLoading?: boolean;

  verifyOTPLoading?: boolean;
  verifyOTPResponse?: IOTPSession;

  emailOTPSessionLoading?: boolean;
  emailOTPSessionError?: any;
  emailOTPSession?: IOTPSession;
  resendEmailOTPSessionLoading?: boolean;
  resendEmailOTPSessionError?: any;
  resendEmailOTPSession?: IOTPSession;

  changeMobileOTPSessionLoading?: boolean;
  changeMobileOTPSessionError?: any;
  changeMobileOTPSession?: any;

  changeMobileOTPConfirmLoading?: boolean;
  changeMobileOTPConfirmError?: any;
  changeMobileOTPConfirm?: any;

  resendChangeMobileLoading?: boolean;
  isForcefulMobileChange?: boolean;
}

const initialState: IRegistrationState = lsu.lsGet("registration") || {
  isLoading: false,
  error: null,
  registrationStep: RegistrationStep.SIGN_UP_NOW,
};

const slice = createSlice({
  name: "registration",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = undefined;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.verifyOTPLoading = false;
      state.resendOTPLoading = false;
      state.error = action.payload;
    },
    setRegistrationStep(state, action) {
      state.registrationStep = action.payload;
    },
    setOTPSessionResponse(state, action) {
      state.mobileOTPResponse = action.payload;
      state.isLoading = false;
      state.resendOTPLoading = false;
      state.otpSendDateTime = new Date();
    },
    setMobileNo(state, action) {
      state.mobileNo = action.payload;
    },
    resendOTPStartLoading(state) {
      state.resendOTPLoading = true;
      state.error = undefined;
    },
    verifyOTPStartLoading(state) {
      state.verifyOTPLoading = true;
      state.error = undefined;
    },
    verifyOTPSuccess(state, action) {
      state.verifyOTPResponse = action.payload;
    },
    setUnfinishedRequest(state, action) {
      state.dsUnfinishedRequest = action.payload;
    },
    emailOTPSessionStartLoading(state) {
      state.emailOTPSessionLoading = true;
      state.emailOTPSessionError = undefined;
    },
    emailOTPSessionHasError(state, action) {
      state.emailOTPSessionLoading = false;
      state.emailOTPSessionError = action.payload;
    },
    emailOTPSessionSuccess(state, action) {
      state.emailOTPSession = action.payload;
      state.emailOTPSessionLoading = false;
      state.otpSendDateTime = new Date();
    },
    resendEmailOTPSessionStartLoading(state) {
      state.resendEmailOTPSessionLoading = true;
      state.resendEmailOTPSessionError = undefined;
    },
    resendEmailOTPSessionHasError(state, action) {
      state.resendEmailOTPSessionLoading = false;
      state.resendEmailOTPSessionError = action.payload;
    },
    resendEmailOTPSessionSuccess(state, action) {
      state.resendEmailOTPSession = action.payload;
      state.resendEmailOTPSessionLoading = false;
      state.otpSendDateTime = new Date();
    },
    changeMobileOTPSessionStartLoading(state) {
      state.changeMobileOTPSessionLoading = true;
    },
    changeMobileOTPSessionHasError(state, action) {
      state.changeMobileOTPSessionLoading = false;
      state.changeMobileOTPSessionError = action.payload;
    },
    changeMobileOTPSessionSuccess(state, action) {
      state.changeMobileOTPSession = action.payload;
      state.changeMobileOTPSessionLoading = false;
      state.otpSendDateTime = new Date();
    },
    changeMobileOTPConfirmStartLoading(state) {
      state.changeMobileOTPConfirmLoading = true;
    },
    changeMobileOTPConfirmHasError(state, action) {
      state.changeMobileOTPConfirmLoading = false;
      state.changeMobileOTPConfirmError = action.payload;
    },
    changeMobileOTPConfirmSuccess(state, action) {
      state.changeMobileOTPConfirm = action.payload;
      state.changeMobileOTPConfirmLoading = false;
    },
    setResendLoading(state, action) {
      state.resendChangeMobileLoading = action.payload;
      state.changeMobileOTPConfirmError = undefined;
    },
    setForcefulMobileChange(state, action) {
      state.isForcefulMobileChange = action.payload;
    },
    setImpersonatedSession(state, action) {
      state.isImpersonatedSession = action.payload;
    },
    reset() {
      return { ...initialState };
    },
  },
});

// Reducer
export default slice.reducer;

export const { reset, setImpersonatedSession } = slice.actions;

export const setRegistrationStep = (registrationStep: RegistrationStep) => {
  return async () => {
    try {
      dispatch(slice.actions.setRegistrationStep(registrationStep));
    } catch (ex) {
      dispatch(slice.actions.hasError(ex));
    }
  };
};

export const sendMobileOTP = (mobileNo: string) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      const { initialQueryParams } = getState().common;
      await dispatch(slice.actions.startLoading());
      let response = await api.sendOTPToMobile({
        CMN: mobileNo,
        AOPRID: initialQueryParams?.AOPRID || 0,
        AOPRUID: initialQueryParams?.AOPRUID || "",
        BPC: initialQueryParams?.BPC || "",
      });
      if (!response.Status) throw new Error(response.Message);
      await dispatch(slice.actions.setOTPSessionResponse(response));
      await dispatch(
        slice.actions.setRegistrationStep(RegistrationStep.CONFIRM_OTP)
      );
    } catch (ex) {
      dispatch(slice.actions.hasError(ex));
    }
  };
};

export const resendMobileOTP = () => {
  return async (disp: any, getState: () => RootState) => {
    try {
      const objOTPSession = getState().registration.mobileOTPResponse;
      if (!objOTPSession) throw new Error(`OTP session is not found`);

      await dispatch(slice.actions.resendOTPStartLoading());
      let response = await api.resendMobileOTP({
        SOTPR: JSON.stringify(objOTPSession),
      });
      if (!response.Status) throw new Error(response.Message);
      await dispatch(slice.actions.setOTPSessionResponse(response));
    } catch (ex) {
      dispatch(slice.actions.hasError(ex));
    }
  };
};

export const verifyMobileOTP = (
  mobileOTP: string,
  objImpersonate?: ImpersonateLoginInsertObject
) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      await dispatch(slice.actions.verifyOTPStartLoading());
      let response: IOTPSession;
      if (objImpersonate) {
        response = await api.ImpersonateLogin(objImpersonate);
        dispatch(setImpersonatedSession(true));
        // const objAuthContext = new AuthInfoContext(
        //   response.ContextSystemObjectIDOrCode
        // );
        // if (!objAuthContext.Status) throw new Error(objAuthContext.Message);
      } else {
        const objOTPSession = getState().registration.mobileOTPResponse;
        if (!objOTPSession) throw new Error(`OTP session is not found`);
        response = await api.verifyMobileOTP({
          MOTP: mobileOTP,
          SOTPR: JSON.stringify(objOTPSession),
        });
      }

      if (!response.Status)
        throw new Error(response.Message || "Unexpected Error");
      if (!response.WasOTPValid)
        throw new Error(response.OTPVerificationMessage || "OTP was not valid");
      await dispatch(slice.actions.verifyOTPSuccess(response));

      await dispatch(setAuthInfo(response));
      const dsUnfinishedRequest = await api.getUnfinishedAOROrAOPR({
        AOSAI: JSON.stringify(response),
        ARAOPR: 1,
      });
      await dispatch(slice.actions.setUnfinishedRequest(dsUnfinishedRequest));
      if (
        dsUnfinishedRequest.Records?.length > 0 ||
        dsUnfinishedRequest.Records1?.length > 0
      ) {
        await dispatch(
          slice.actions.setRegistrationStep(
            RegistrationStep.SELECT_UNFINISHED_AOR
          )
        );
      } else {
        await dispatch(
          slice.actions.setRegistrationStep(RegistrationStep.YOUR_DETAILS)
        );
      }
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.hasError(ex));
    }
  };
};

export const onSelectAOPR = (objAOPR: IAOPR) => {
  return async (disp: any, getState: () => RootState) => {
    await dispatch(setSelectedAOPR(objAOPR));
    await dispatch(
      slice.actions.setRegistrationStep(RegistrationStep.YOUR_DETAILS)
    );
  };
};

export const onSelectAOR = (objAOR: IAOR, showChangeMobile?: boolean) => {
  return async (disp: any, getState: () => RootState) => {
    await dispatch(setSelectedAOR(objAOR));
    if (
      objAOR.CommonMobileCheckRWPBSInternalCode ===
        Enums.RW_PROCESS_BASIC_STATUS.FAIL ||
      showChangeMobile
    ) {
      await dispatch(slice.actions.setForcefulMobileChange(!showChangeMobile));
      await dispatch(
        slice.actions.setRegistrationStep(RegistrationStep.CHANGE_MOBILE_NUMBER)
      );
    } else {
      await dispatch(
        slice.actions.setRegistrationStep(RegistrationStep.YOUR_DETAILS)
      );
    }
  };
};

export const setNameEmail = (info: {
  pan: string;
  dob: string | null;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
}) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      await dispatch(slice.actions.startLoading());
      const { objAOR, objAOPR } = getState().common;
      const { verifyOTPResponse } = getState().registration;
      const io = new SetNameEmailInsertObject();
      if (objAOR) {
        io.AORID = objAOR.AccountOpeningRequestID;
        io.AORUID = objAOR.AccountOpeningRequestUID;
      } else if (objAOPR) {
        io.AOPRID = objAOPR.AccountOpeningPrefilledRequestID;
        io.AOPRUID = objAOPR.AccountOpeningPrefilledRequestUID;
      }
      io.CPAN = info.pan;
      // io.CDOB = utils.convertToYYYYMMDDFormat(info.dob);
      io.CDOB = info.dob ? info.dob : "";
      io.CFN = info.firstName;
      io.CMN = info.middleName;
      io.CLN = info.lastName;
      io.CEID = info.email;
      io.AOSAI = JSON.stringify(verifyOTPResponse);
      const result = await api.setNameEmail(io);
      const objAuthContext = new AuthInfoContext(
        result.ContextSystemObjectIDOrCode
      );
      if (!objAuthContext.Status) {
        throw new Error(
          objAuthContext.Message ||
            "Unexpected error occurred while setting basic info."
        );
      }
      await dispatch(setAuthInfo(result));
      if (objAuthContext.NextStage < 1 && result.MemoryBag) {
        await dispatch(slice.actions.emailOTPSessionSuccess(result.MemoryBag));
        await dispatch(
          slice.actions.setRegistrationStep(RegistrationStep.CONFIRM_EMAIL_OTP)
        );
        return;
      } else if (objAuthContext.NextStage >= 1) {
        await dispatch(setStage(objAuthContext.NextStage));
      } else {
        throw new Error("Unexpected stage identified");
      }
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.hasError(ex));
    }
  };
};

export const verifyEmailOTP = (emailOTP: string) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      const objOTPSession = getState().registration.emailOTPSession;
      const { objAuthInfoResponse } = getState().common;
      if (!objOTPSession) throw new Error(`OTP session is not found`);
      await dispatch(slice.actions.emailOTPSessionStartLoading());
      let response: IOTPSession = await api.verifyEmailOTP({
        EOTP: emailOTP,
        SOTPR: JSON.stringify(objOTPSession),
        AOSAI: JSON.stringify(objAuthInfoResponse),
      });

      const objAuthContext = new AuthInfoContext(
        response.ContextSystemObjectIDOrCode
      );

      if (!objAuthContext.Status)
        throw new Error(objAuthContext.Message || "Unexpected Error");
      if (!response.WasOTPValid)
        throw new Error(response.OTPVerificationMessage || "OTP was not valid");

      await dispatch(slice.actions.emailOTPSessionSuccess(response)); // Just for reference. may not be used anywhere
      await dispatch(setAuthInfo(response));
      if (objAuthContext.NextStage > 0) {
        await dispatch(setStage(objAuthContext.NextStage));
      } else {
        throw new Error("Unexpected stage identified");
      }
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.emailOTPSessionHasError(ex));
    }
  };
};

export const resendEmailOTP = () => {
  return async (disp: any, getState: () => RootState) => {
    try {
      const { emailOTPSession } = getState().registration;
      const { objAuthInfoResponse } = getState().common;
      if (!emailOTPSession) throw new Error(`OTP session is not found`);

      await dispatch(slice.actions.resendEmailOTPSessionStartLoading());
      let response = await api.resendEmailOTP({
        SOTPR: JSON.stringify(emailOTPSession),
        AOSAI: JSON.stringify(objAuthInfoResponse),
      });
      if (!response.Status) throw new Error(response.Message);
      await dispatch(slice.actions.resendEmailOTPSessionSuccess(response));
    } catch (ex) {
      dispatch(slice.actions.resendEmailOTPSessionHasError(ex));
    }
  };
};

export const sendChangeMobileOTP = (mobileNo: string) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      await dispatch(slice.actions.changeMobileOTPSessionStartLoading());
      const { objAuthInfoResponse, objAOR } = getState().common;
      if (!objAOR) throw new Error("Account opening request not found");
      let response = await api.changeMobileNumber({
        NCMN: mobileNo,
        AORID: objAOR.AccountOpeningRequestID,
        AORUID: objAOR.AccountOpeningRequestUID,
        AOSAI: JSON.stringify(objAuthInfoResponse),
      });
      const objAuthContext = new AuthInfoContext(
        response.ContextSystemObjectIDOrCode
      );

      if (!objAuthContext.Status)
        throw new Error(objAuthContext.Message || "Unexpected Error");
      if (!response.MemoryBag)
        throw new Error("OTP session was not returned from server.");

      await dispatch(setAuthInfo(response));
      await dispatch(
        slice.actions.changeMobileOTPSessionSuccess(response.MemoryBag)
      );
      await dispatch(
        slice.actions.setRegistrationStep(
          RegistrationStep.CONFIRMED_CHANGED_MOBILE
        )
      );
    } catch (ex) {
      dispatch(slice.actions.changeMobileOTPSessionHasError(ex));
    }
  };
};

export const resendChangeMobileOTP = () => {
  return async (disp: any, getState: () => RootState) => {
    try {
      const objOTPSession = getState().registration.changeMobileOTPSession;
      const { objAuthInfoResponse, objAOR } = getState().common;
      if (!objAOR) throw new Error("Account opening request not found");
      if (!objOTPSession) throw new Error(`OTP session is not found`);

      await dispatch(slice.actions.setResendLoading(true));
      let response = await api.resendChangeMobileOTP({
        SOTPR: JSON.stringify(objOTPSession),
        AOSAI: JSON.stringify(objAuthInfoResponse),
      });
      if (!response.Status) throw new Error(response.Message);
      await dispatch(slice.actions.changeMobileOTPSessionSuccess(response));
      await dispatch(slice.actions.setResendLoading(false));
    } catch (ex) {
      dispatch(slice.actions.changeMobileOTPSessionHasError(ex));
      await dispatch(slice.actions.setResendLoading(false));
    }
  };
};

export const verifyChangeMobileOTP = (mobileOTP: string) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      const objOTPSession = getState().registration.changeMobileOTPSession;
      if (!objOTPSession) throw new Error(`OTP session is not found`);
      const { objAuthInfoResponse, objAOR } = getState().common;
      if (!objAOR) throw new Error("Account opening request not found");

      await dispatch(slice.actions.changeMobileOTPConfirmStartLoading());
      let response: IOTPSession = await api.verifyChangeMobileOTP({
        MOTP: mobileOTP,
        SOTPR: JSON.stringify(objOTPSession),
        AOSAI: JSON.stringify(objAuthInfoResponse),
      });

      if (!response.Status) {
        throw new Error(response.Message || "Unexpected Error");
      }
      // if (!response.WasOTPValid)
      //   throw new Error(response.OTPVerificationMessage || "OTP was not valid");
      await dispatch(slice.actions.changeMobileOTPConfirmSuccess(response));
      alert("Mobile number change successfully. Please login with your new mobile number.");
      await dispatch(logOutAndResetFields());
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.changeMobileOTPConfirmHasError(ex));
    }
  };
};
